import React from 'react'
import Table from '../../table'
import {
  PayrollWeeksTableProps,
  buildWeekRowData,
  useWeeksTableColumns,
  calculateTotals,
  filterEventsByOccurrence,
  createTotalRow,
} from './index'
import { uniqBy } from 'lodash'
import DailyWorkPeriodsTable from '../../work-periods/daily-work-periods-table'
import { WorkPeriodWithSummary } from '../../../api/work-periods'

/**
 * Composant pour afficher les données de paie par semaine pour un worker
 *
 * Affiche un tableau avec une ligne par semaine contenant :
 * - Numéro de semaine et année
 * - Période de la semaine
 * - Heures contractuelles vs travaillées
 * - Heures supplémentaires
 * - Pauses
 * - Statut de validation et enregistrement
 *
 * **Row expansion:** Uses the shared {@link Table} expand API. Each non-total week row with a
 * `weekSummary` can be expanded to show {@link DailyWorkPeriodsTable} (one row per work period).
 */
const PayrollWeeksTable: React.FC<PayrollWeeksTableProps> = ({
  worker,
  weeks,
  events,
  onCloseWeek,
}) => {
  // Récupérer les employer rules depuis la première semaine disponible
  const employerRules = uniqBy(
    worker.weeksSummary?.weeks.flatMap(w => w.employerRules) || [],
    'ruleId',
  )
  // Calculer les totaux
  const {
    totalBenefitEvents,
    totalInformativeEvents,
    totalInformativeEventsByEvent,
    totalEmployerRulesValues,
    totalEmployerRulesDecimalValues,
    negativeContractDiff,
  } = calculateTotals(weeks, worker, events, employerRules)

  // Filtrer les events par occurrence
  const { visibleBenefitEvents } = filterEventsByOccurrence(events, totalBenefitEvents)

  const hasInformativeEvents = totalInformativeEvents > 0

  const tableColumns = useWeeksTableColumns(
    worker.weeklyHoursWorked,
    employerRules,
    visibleBenefitEvents,
    hasInformativeEvents,
    onCloseWeek,
  )

  // Transformation des données en lignes de tableau
  const data = React.useMemo(
    () => weeks.map(week => buildWeekRowData(week, worker, events, employerRules)),
    [weeks, worker, events, employerRules],
  )

  // Ajouter la ligne de totaux
  data.push(
    createTotalRow(
      data,
      worker,
      totalEmployerRulesValues,
      totalEmployerRulesDecimalValues,
      totalBenefitEvents,
      totalInformativeEvents,
      totalInformativeEventsByEvent,
      events,
      negativeContractDiff,
    ),
  )

  return (
    <>
      <Table
        columns={tableColumns}
        data={data}
        noDataMessage="Aucune semaine à afficher"
        dataTest="payroll-weeks-table"
        getRowKey={({ original }) =>
          original.isTotalRow
            ? 'payroll-weeks-total'
            : `${original.weekNumberValue ?? 'wk'}-${original.yearValue ?? 'yr'}`
        }
        getRowCanExpand={({ original }) => !original.isTotalRow && Boolean(original.weekSummary)}
        renderExpandedRow={({ original }) => {
          const weekSummary = original.weekSummary
          if (!weekSummary) {
            return null
          }
          const workPeriods = weekSummary.missions.flatMap(
            m => m.workPeriods,
          ) as unknown as WorkPeriodWithSummary[]
          const dailyRules = weekSummary.employerRules.filter(r => r.display?.summary?.days)
          return (
            <DailyWorkPeriodsTable
              workPeriods={workPeriods}
              employerRules={dailyRules}
              dayTotals={weekSummary.totals.days}
              dataTest="payroll-week-daily-work-periods-table"
            />
          )
        }}
      />
    </>
  )
}

export default PayrollWeeksTable
